/**
 * Financial Summary Cards Component
 * 
 * Displays sticky cards showing Planned vs Realized for:
 * - Receitas (Revenues)
 * - Despesas Futuras (Future Expenses)
 * - Despesas Mensais (Monthly Expenses)
 * - Despesas Dia a Dia (Daily Expenses)
 * - Saldo (Balance)
 * 
 * Variation Logic:
 * - Receitas e Despesas Futuras: positive when realized > planned (green)
 * - Despesas Mensais, Dia a Dia e Saldo: positive when planned > realized (green)
 * - Negative values show with "-" sign in red
 * 
 * Based on client requirement: CorreçõesLibrattum05122025.pdf Item 3
 */
import type { ElementType } from 'react';
import { DollarSign, TrendingUp, PiggyBank, Calendar, Wallet } from 'lucide-react';
import { useDashboard, useCurrentBudgetPeriod } from '@/hooks/use-financial';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocation, useNavigate } from 'react-router-dom';

interface SummaryCardProps {
  title: string;
  plannedValue: number;
  realizedValue: number;
  icon: ElementType;
  variant: 'revenue' | 'expense' | 'future' | 'daily' | 'balance';
  onClick?: () => void;
  isActive?: boolean;
  showVariation?: boolean;
  tourId?: string;
  variationTourId?: string;
}

/**
 * Calculate the variation value based on the card variant.
 * 
 * - Receitas e Despesas Futuras: realized - planned (positive when realized > planned)
 * - Despesas Mensais, Dia a Dia e Saldo: planned - realized (positive when planned > realized)
 */
const calculateVariation = (
  variant: SummaryCardProps['variant'],
  plannedValue: number,
  realizedValue: number
): number => {
  if (variant === 'revenue' || variant === 'future') {
    // For revenues and future expenses: positive when realized > planned
    return realizedValue - plannedValue;
  }
  // For monthly expenses, daily expenses, and balance: positive when planned > realized
  return plannedValue - realizedValue;
};

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const SummaryCard = ({
  title,
  plannedValue,
  realizedValue,
  icon: Icon,
  variant,
  onClick,
  isActive = false,
  showVariation = true,
  tourId,
  variationTourId,
}: SummaryCardProps) => {
  const variantStyles = {
    revenue: {
      border: 'border-emerald-200 dark:border-emerald-800',
      // Fundo base branco + destaque ativo
      bgBase: 'bg-white dark:bg-card',
      bgActive: 'bg-emerald-50 dark:bg-emerald-950/30',
      accent: 'bg-emerald-500',
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      titleColor: 'text-emerald-700 dark:text-emerald-300',
    },
    future: {
      border: 'border-slate-200 dark:border-slate-800',
      // Lilás claro solicitado (#D3DAF0) ao ficar ativo
      bgBase: 'bg-white dark:bg-card',
      bgActive: 'bg-[#D3DAF0] dark:bg-[#D3DAF0]/25',
      accent: 'bg-slate-500',
      iconBg: 'bg-slate-100 dark:bg-slate-900/50',
      iconColor: 'text-slate-600 dark:text-slate-300',
      titleColor: 'text-slate-700 dark:text-slate-300',
    },
    expense: {
      border: 'border-orange-200 dark:border-orange-800',
      // Cor solicitada para Despesas Mensais (#fffdf8) ao ficar ativo
      bgBase: 'bg-white dark:bg-card',
      bgActive: 'bg-[#fffdf8] dark:bg-[#fffdf8]/10',
      accent: 'bg-orange-500',
      iconBg: 'bg-orange-100 dark:bg-orange-900/50',
      iconColor: 'text-orange-600 dark:text-orange-400',
      titleColor: 'text-orange-700 dark:text-orange-300',
    },
    daily: {
      border: 'border-blue-200 dark:border-blue-800',
      // Cor solicitada para Dia a Dia (#f4feff) ao ficar ativo
      bgBase: 'bg-white dark:bg-card',
      bgActive: 'bg-[#f4feff] dark:bg-[#f4feff]/15',
      accent: 'bg-blue-500',
      iconBg: 'bg-blue-100 dark:bg-blue-900/50',
      iconColor: 'text-blue-600 dark:text-blue-400',
      titleColor: 'text-blue-700 dark:text-blue-300',
    },
    balance: {
      border: 'border-green-200 dark:border-green-800',
      bgBase: 'bg-white dark:bg-card',
      bgActive: 'bg-green-50/70 dark:bg-green-950/25',
      accent: 'bg-green-500',
      iconBg: 'bg-green-100 dark:bg-green-900/50',
      iconColor: 'text-green-600 dark:text-green-400',
      titleColor: 'text-green-700 dark:text-green-300',
    },
  };

  const styles = variantStyles[variant];
  const isClickable = typeof onClick === 'function';
  
  // Calculate variation based on variant type
  const variation = calculateVariation(variant, plannedValue, realizedValue);
  const isVariationPositive = variation >= 0;

  const plannedIsNegative = plannedValue < 0;
  const realizedIsNegative = realizedValue < 0;

  // Regra do cliente: quando o saldo REALIZADO for negativo, destacar também o fundo do card.
  const forceNegativeBackground = variant === 'balance' && realizedIsNegative;
  const obterFundo = () => {
    if (forceNegativeBackground) return 'bg-rose-50 dark:bg-rose-950/25';
    return isActive ? styles.bgActive : styles.bgBase;
  };
  const bgClass = obterFundo();
  const borderClass = forceNegativeBackground ? 'border-rose-300 dark:border-rose-800' : styles.border;
  const accentClass = forceNegativeBackground ? 'bg-rose-500' : styles.accent;

  // Format variation with sign for display
  const formatVariation = (value: number): string => {
    const absValue = Math.abs(value);
    const formatted = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(absValue);
    
    if (value < 0) {
      return `- ${formatted}`;
    }
    return formatted;
  };

  return (
    <div
      className={`relative rounded-lg border ${borderClass} ${bgClass} p-2.5 shadow-sm transition-all duration-200 ${
        isClickable
          ? 'cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          : ''
      } ${isActive ? 'ring-2 ring-primary/20' : ''}`}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!isClickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={isClickable ? `Abrir ${title}` : undefined}
      data-tour-id={tourId}
    >
      {/* Top accent line */}
      <div className={`absolute top-0 left-3 right-3 h-0.5 rounded-full ${accentClass}`} />
      
      {/* Header with icon */}
      <div className="flex items-center gap-1.5 mb-2 pt-0.5">
        <div className={`p-1.5 rounded-md ${styles.iconBg}`}>
          <Icon className={`h-3.5 w-3.5 ${styles.iconColor}`} />
        </div>
        <span className={`text-sm font-semibold ${styles.titleColor}`}>{title}</span>
      </div>
      
      {/* Planned value */}
      <div className="space-y-1.5">
        <div className="space-y-0.5">
          <span className="text-xs text-muted-foreground">Planejado</span>
          <div className="rounded-md border border-border/60 bg-white/60 px-2 py-1 dark:bg-card/70">
            <div className={`text-right tabular-nums font-bold text-sm sm:text-base ${variant === 'balance' && plannedIsNegative ? 'text-rose-600 dark:text-rose-400' : 'text-foreground'}`}>
              {formatCurrency(plannedValue)}
            </div>
          </div>
        </div>

        <div className="space-y-0.5">
          <span className="text-xs text-muted-foreground">Realizado</span>
          <div className="rounded-md border border-border/60 bg-white/60 px-2 py-1 dark:bg-card/70">
            <div className={`text-right tabular-nums font-bold text-sm sm:text-base ${variant === 'balance' && realizedIsNegative ? 'text-rose-600 dark:text-rose-400' : 'text-foreground'}`}>
              {formatCurrency(realizedValue)}
            </div>
          </div>
        </div>

        {showVariation && (
          <div
            className="flex items-center justify-between pt-0.5"
            data-tour-id={variationTourId}
          >
            <span className="text-xs text-muted-foreground">Variação</span>
            <span className={`text-xs font-semibold tabular-nums ${
              isVariationPositive 
                ? 'text-emerald-600 dark:text-emerald-400' 
                : 'text-rose-600 dark:text-rose-400'
            }`}>
              {formatVariation(variation)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

interface FinancialSummaryCardsProps {
  periodId?: string;
  className?: string;
}

export function FinancialSummaryCards({ periodId, className = '' }: Readonly<FinancialSummaryCardsProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: currentPeriod } = useCurrentBudgetPeriod();
  const effectivePeriodId = periodId || currentPeriod?.id;
  const { data: dashboardData, isLoading } = useDashboard(effectivePeriodId);

  const kpis = dashboardData?.kpis;

  if (isLoading) {
    return (
      <section className={`md:sticky md:top-0 md:z-20 md:-mx-6 md:px-6 py-2 md:bg-background/95 md:backdrop-blur md:supports-[backdrop-filter]:bg-background/60 md:border-b md:border-border/40 ${className}`}>
        <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-border p-2.5">
              <Skeleton className="h-3 w-20 mb-2" />
              <Skeleton className="h-5 w-full mb-1.5" />
              <Skeleton className="h-5 w-full" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Calculate values from KPIs
  const revenuesPlanned = Number(kpis?.total_planned_revenue) || 0;
  const revenuesRealized = Number(kpis?.total_realized_revenue) || 0;
  
  const futureExpensesPlanned = Number(kpis?.total_planned_future_expenses) || 0;
  const futureExpensesRealized = Number(kpis?.total_realized_future_expenses) || 0;
  
  const monthlyExpensesPlanned = Number(kpis?.total_planned_monthly_expenses) || 0;
  const monthlyExpensesRealized = Number(kpis?.total_realized_monthly_expenses) || 0;
  
  const dailyExpensesPlanned = Number(kpis?.total_planned_daily_expenses) || 0;
  const dailyExpensesRealized = Number(kpis?.total_realized_daily_expenses) || 0;
  
  const balancePlanned = revenuesPlanned - (futureExpensesPlanned + monthlyExpensesPlanned + dailyExpensesPlanned);
  const balanceRealized = revenuesRealized - (futureExpensesRealized + monthlyExpensesRealized + dailyExpensesRealized);

  const openSheet = (to: string) => {
    if (location.pathname === to) {
      const el = document.getElementById('sheet');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el?.querySelector<HTMLInputElement>('input')?.focus?.();
      return;
    }
    navigate(`${to}#sheet`);
  };

  const activePath = location.pathname;
  const CARTAO_POR_PREFIXO: Array<[string, SummaryCardProps['variant']]> = [
    ['/financial/revenues', 'revenue'],
    ['/financial/future-expenses', 'future'],
    ['/financial/monthly-expenses', 'expense'],
    ['/financial/daily-expenses', 'daily'],
  ];
  const activeCard: SummaryCardProps['variant'] | null =
    CARTAO_POR_PREFIXO.find(([prefixo]) => activePath.startsWith(prefixo))?.[1] ?? null;

  return (
    <section className={`md:sticky md:top-0 md:z-20 md:-mx-6 md:px-6 py-2 md:bg-background/95 md:backdrop-blur md:supports-[backdrop-filter]:bg-background/60 md:border-b md:border-border/40 md:shadow-sm ${className}`}>
      <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
        <SummaryCard
          title="Receitas"
          plannedValue={revenuesPlanned}
          realizedValue={revenuesRealized}
          icon={TrendingUp}
          variant="revenue"
          isActive={activeCard === 'revenue'}
          onClick={() => openSheet('/financial/revenues')}
          tourId="summary-revenue"
          variationTourId="summary-revenue-variation"
        />
        <SummaryCard
          title="Despesas Futuras"
          plannedValue={futureExpensesPlanned}
          realizedValue={futureExpensesRealized}
          icon={PiggyBank}
          variant="future"
          isActive={activeCard === 'future'}
          onClick={() => openSheet('/financial/future-expenses')}
          tourId="summary-future"
        />
        <SummaryCard
          title="Despesas Mensais"
          plannedValue={monthlyExpensesPlanned}
          realizedValue={monthlyExpensesRealized}
          icon={Calendar}
          variant="expense"
          isActive={activeCard === 'expense'}
          onClick={() => openSheet('/financial/monthly-expenses')}
          tourId="summary-monthly"
        />
        <SummaryCard
          title="Despesas Dia a Dia"
          plannedValue={dailyExpensesPlanned}
          realizedValue={dailyExpensesRealized}
          icon={DollarSign}
          variant="daily"
          isActive={activeCard === 'daily'}
          onClick={() => openSheet('/financial/daily-expenses')}
          tourId="summary-daily"
        />
        <SummaryCard
          title="Saldo"
          plannedValue={balancePlanned}
          realizedValue={balanceRealized}
          icon={Wallet}
          variant="balance"
          showVariation={false}
          tourId="summary-balance"
        />
      </div>
    </section>
  );
}

export default FinancialSummaryCards;


